import { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDate, toNumericAmount } from '../../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import {
  filterSettlementStatusOptions,
  getApprovalStatusLabel,
} from '../../utils/saleStatusAdapter';
import { trackPosSessionOpened } from '../../analytics/events';
import { useAnalyticsContext } from '../../analytics/useAnalyticsContext';
import * as XLSX from 'xlsx';
import { getSaleLoanIdentifierDisplay } from '../../utils/saleLoanIdentifier';
import { getVisiblePageNumbers } from '../../utils/getVisiblePageNumbers';

const MySwal = withReactContent(Swal);

const INITIAL_SALES_FILTERS = {
  search: '',
  approval_status: '',
  settlement_status: '',
  from_date: '',
  to_date: '',
};

export function useSalesList({ includeBranchFilter = false } = {}) {
  const navigate = useNavigate();
  const { commerceId, branchId, channel } = useAnalyticsContext();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1,
  });
  const [filters, setFilters] = useState({
    ...INITIAL_SALES_FILTERS,
    ...(includeBranchFilter ? { branch_id: '' } : {}),
  });
  const [appliedFilters, setAppliedFilters] = useState({
    ...INITIAL_SALES_FILTERS,
    ...(includeBranchFilter ? { branch_id: '' } : {}),
  });
  const [approvalStatusOptions, setApprovalStatusOptions] = useState([]);
  const [settlementStatusOptions, setSettlementStatusOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  useEffect(() => {
    if (commerceId) {
      trackPosSessionOpened({ commerceId, branchId, channel });
    }
  }, [commerceId, branchId, channel]);

  useEffect(() => {
    fetchSales();
  }, [appliedFilters, pagination.current_page]);

  const normalizeSearchForApi = (search) => {
    const trimmed = (search || '').trim();
    return trimmed.length === 0 || trimmed.length >= 3 ? trimmed : '';
  };

  const buildSalesParams = (page, perPage = pagination.per_page, filterSource = appliedFilters) => {
    const params = {
      search: normalizeSearchForApi(filterSource.search),
      approval_status: filterSource.approval_status || '',
      settlement_status: filterSource.settlement_status || '',
      from_date: filterSource.from_date || '',
      to_date: filterSource.to_date || '',
      page,
      per_page: perPage,
    };
    if (includeBranchFilter && filterSource.branch_id) {
      params.branch_id = filterSource.branch_id;
    }
    return new URLSearchParams(params);
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = buildSalesParams(pagination.current_page, pagination.per_page);
      const response = await api.get(`/sales?${params}`);
      setSales(response.data.sales);
      setPagination(response.data.pagination);
      if (response.data.filters?.approval_status_options?.length) {
        setApprovalStatusOptions(response.data.filters.approval_status_options);
      }
      if (response.data.filters?.settlement_status_options?.length) {
        setSettlementStatusOptions(
          filterSettlementStatusOptions(response.data.filters.settlement_status_options)
        );
      }
      if (includeBranchFilter && Array.isArray(response.data.filters?.branch_options)) {
        setBranchOptions(response.data.filters.branch_options);
      }
    } catch (error) {
      console.error('Error al obtener ventas:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las ventas',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    const trimmedSearch = (filters.search || '').trim();
    if (trimmedSearch.length > 0 && trimmedSearch.length < 3) {
      MySwal.fire({
        title: 'Búsqueda',
        text: 'Ingresá al menos 3 caracteres para buscar por cliente, DNI o N° préstamo.',
        icon: 'info',
      });
      return;
    }
    setAppliedFilters({
      ...filters,
      search: trimmedSearch,
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    const cleared = {
      ...INITIAL_SALES_FILTERS,
      ...(includeBranchFilter ? { branch_id: '' } : {}),
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleViewDetail = (saleId, customerId) => {
    setSelectedSale({ id: saleId, customer: { id: customerId } });
    setShowDetailModal(true);
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const buildExportRow = (sale) => {
    const row = {
      'N° Préstamo': getSaleLoanIdentifierDisplay(sale),
      Cliente: sale.customer?.name || '',
      DNI: sale.customer?.dni || '',
      ...(includeBranchFilter ? { Sucursal: sale.branch?.name || '' } : {}),
      Fecha: formatDate(sale.sale_date),
      Vendedor: sale.seller?.full_name || '',
      Monto: toNumericAmount(sale.amount),
      Cuotas: sale.installments,
      'Estado Aprobación (Préstamo)': getApprovalStatusLabel(sale),
      'Fecha Liquidación': sale.settlement_date ? formatDate(sale.settlement_date) : '',
    };
    return row;
  };

  const handleExportExcel = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      const perPage = pagination.per_page || 25;
      const firstResponse = await api.get(`/sales?${buildSalesParams(1, perPage)}`);
      const firstPageSales = firstResponse?.data?.sales || [];
      const firstPagination = firstResponse?.data?.pagination || {};
      const totalSales = Number(firstPagination.total || 0);
      const lastPage = Number(firstPagination.last_page || 1);

      if (totalSales === 0 || firstPageSales.length === 0) {
        MySwal.fire({
          title: 'Sin datos',
          text: 'No hay ventas para exportar con los filtros actuales.',
          icon: 'info',
        });
        return;
      }

      let allSales = [...firstPageSales];
      for (let page = 2; page <= lastPage; page += 1) {
        const response = await api.get(`/sales?${buildSalesParams(page, perPage)}`);
        allSales = allSales.concat(response?.data?.sales || []);
      }

      const rows = allSales.map((sale) => buildExportRow(sale));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas');

      const dateString = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `Ventas_${dateString}.xlsx`);
    } catch (error) {
      console.error('Error al exportar ventas:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudo exportar el archivo de ventas.',
        icon: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const Pagination = ({ loading: paginationLoading }) => {
    const visiblePages = getVisiblePageNumbers(pagination.current_page, pagination.last_page, 5);

    return (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-end flex-wrap mb-0">
        <li className={`page-item ${paginationLoading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPagination((prev) => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={paginationLoading}
          >
            Anterior
          </button>
        </li>
        {visiblePages.map((page) => (
          <li
            key={page}
            className={`page-item ${pagination.current_page === page ? 'active' : ''} ${paginationLoading ? 'disabled' : ''}`}
          >
            <button
              type="button"
              className="page-link"
              onClick={() => setPagination((prev) => ({ ...prev, current_page: page }))}
              disabled={paginationLoading}
            >
              {page}
            </button>
          </li>
        ))}
        <li className={`page-item ${paginationLoading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPagination((prev) => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={paginationLoading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
    );
  };

  return {
    sales,
    loading,
    selectedSale,
    showDetailModal,
    setShowDetailModal,
    setSelectedSale,
    exporting,
    pagination,
    filters,
    branchOptions,
    approvalStatusOptions,
    settlementStatusOptions,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    handleViewDetail,
    handleGoToCustomer,
    handleExportExcel,
    Pagination,
  };
}
